// @flow

import React from "react";

import EditableForm from "../../components/EditableForm";
import FormRow from "../../components/FormRow";
import ConditionalSubform from "../../components/ConditionalSubform";
import EditableField from "../../components/EditableField";
import * as formConditions from "../../utils/formConditions";
import * as mappers from "../../utils/mappers";
import * as v from "../../utils/validators";
import * as formatters from "../../utils/formatters";
import { errors } from "../../utils/errors";
import { placeholders } from "../../utils/placeholders";

import {
  PURPOSE_KIND,
  MACHINE_PRICE,
  MACHINE_CATEGORY,
  MACHINE_IS_ORDERED,
  MACHINE_DELIVERY_DATE,
  MACHINE_AGE,
  MACHINE_IS_NEW,
  MACHINE_ORIGINAL_PRICE,
  MACHINE_PERIOD_OF_USE,
  MACHINE_MANUFACTURER,
  MACHINE_KIND,
  FEDERAL_STATE,
  MACHINE_DESCRIPTION
} from "./formFields";
import { mapPurposeKind, getKind, mapMachineCategory } from "./mappers";

import { type InvestmentSectionProps } from "../../types/details";

const InvestmentMachine = ({
  kind,
  initialValues,
  handleSubmit,
  isLocked
}: InvestmentSectionProps) => {
  return (
    <EditableForm initialValues={initialValues} onSubmit={handleSubmit} isLocked={isLocked}>
      <FormRow separator>
        <EditableField
          fieldControlProps={{
            name: PURPOSE_KIND,
            caption: () => "Investitionsvorhaben"
          }}
          displayValue={value => {
            const purposeKind = getKind(value, kind);
            return purposeKind && mapPurposeKind(purposeKind.kind);
          }}
        />
        <EditableField
          fieldControlProps={{
            name: MACHINE_PRICE,
            caption: () => "Kaufpreis (netto)"
          }}
          displayValue={formatters.formatCurrency}
        />
        <EditableField
          fieldControlProps={{
            name: FEDERAL_STATE,
            caption: () => "Investitionsort liegt in..."
          }}
        />
      </FormRow>
      <FormRow separator>
        <EditableField
          fieldControlProps={{
            name: MACHINE_CATEGORY,
            caption: () => "Kategorie",
            validate: v.combineWithPrefillCheck(errors.requiredField())
          }}
          displayValue={mapMachineCategory}
        />
        <EditableField
          fieldControlProps={{
            name: MACHINE_DESCRIPTION,
            caption: () => "Beschreibung",
            validate: v.combineWithPrefillCheck(
              errors.requiredField(),
              v.maxLength(100, errors.maxLengthCharacters(100))
            )
          }}
          type="textInput"
          inputProps={{ placeholder: "Bitte beschreiben Sie das Finanzierungsobjekt" }}
        />
        <EditableField
          fieldControlProps={{
            name: MACHINE_PERIOD_OF_USE,
            caption: () => "Gewöhnliche/geplante Nutzungsdauer",
            validate: v.combineWithPrefillCheck(
              errors.requiredField(),
              v.upperBound(999, errors.maxLengthDigits(3))
            )
          }}
          displayValue={formatters.formatYears}
          inputProps={{ placeholder: placeholders.inYears, separateThousands: false }}
          type="numberInput"
        />
      </FormRow>
      <FormRow separator>
        <EditableField
          fieldControlProps={{
            name: MACHINE_MANUFACTURER,
            caption: () => "Hersteller"
          }}
        />
        <EditableField
          fieldControlProps={{
            name: MACHINE_KIND,
            caption: () => "Typ"
          }}
        />
        <div />
      </FormRow>
      <FormRow separator>
        <EditableField
          fieldControlProps={{
            name: MACHINE_IS_ORDERED,
            caption: () => "Ist das Objekt bereits bestellt?"
          }}
          displayValue={mappers.yesNoMapper}
        />
        <EditableField
          fieldControlProps={{
            name: MACHINE_DELIVERY_DATE,
            caption: () => "Voraussichtliches Lieferdatum",
            validate: v.combineWithPrefillCheck(errors.requiredField())
          }}
          displayValue={mappers.dateMapper}
        />
        <div />
      </FormRow>
      <FormRow separator>
        <EditableField
          fieldControlProps={{
            name: MACHINE_IS_NEW,
            caption: () => "Neue Maschine/Anlage?"
          }}
          displayValue={mappers.yesNoMapper}
        />
        <ConditionalSubform
          condition={formConditions.fieldHasValue(MACHINE_IS_NEW, false)}
          renderOnSatisfy={() => (
            <>
              <EditableField
                fieldControlProps={{
                  name: MACHINE_AGE,
                  caption: () => "Alter"
                }}
                displayValue={formatters.formatYears}
              />
              <EditableField
                fieldControlProps={{
                  name: MACHINE_ORIGINAL_PRICE,
                  caption: () => "Ursprünglicher Neupreis"
                }}
                displayValue={formatters.formatCurrency}
              />
            </>
          )}
          renderOnFail={() => (
            <>
              <div />
              <div />
            </>
          )}
        />
      </FormRow>
    </EditableForm>
  );
};
export default InvestmentMachine;
